import { Popover, Input, Divider } from 'antd';
import { Building03, LogOut02 } from '@insight-health-ai/roseate/icons';
import { PractitionerName } from 'src/components';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import {
  useAuth,
  useAuthActions,
  AdminPortal,
  useTenantsState,
  ContextHolder,
  useFeatureEntitlements,
} from '@frontegg/react';
import { useState, useMemo } from 'react';
import {
  SettingOutlined,
  SearchOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { WorkspaceLogo, WorkspacePlan } from './workspaceLogo';
import { analytics, logger, RUM } from 'src/services';
import { useIsOP } from 'src/hooks/misc';
import { StripeBillingCustomerPortal } from 'src/modules/ambient_solo/components/Payments/StripeCustomerPortal';

const MenuItem = styled.div<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease;
  ${({ danger, theme }) =>
    danger
      ? `
    color: ${theme.colors.error.DEFAULT};
  `
      : ''}

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary[100]};
  }

  .anticon {
    font-size: 16px;
  }
`;

const UserMenuContent = ({ onClose }: { onClose: () => void }) => {
  const { data: currentPractitioner } = useGetCurrentPractitioner();
  const { isEntitled: isPaymentsFeatureEntitled } =
    useFeatureEntitlements('payments');
  const { user } = useAuth();
  const { tenants } = useTenantsState();
  const { switchTenant } = useAuthActions();
  const isOP = useIsOP();

  const [orgSearch, setOrgSearch] = useState('');

  const activeTenantId = user?.tenantId;

  const filteredTenants = useMemo(() => {
    if (!orgSearch.trim()) return tenants;
    const filtered = tenants.filter((tenant) =>
      tenant.name.toLowerCase().includes(orgSearch.toLowerCase())
    );
    return filtered;
  }, [tenants, orgSearch]);

  const handleSwitchOrg = (tenantId: string) => {
    if (tenantId !== activeTenantId) {
      switchTenant({ tenantId });
    }
    onClose();
  };

  const handleOpenSettings = async () => {
    analytics.track('Viewed Settings', {
      email: user?.email,
      orgId: currentPractitioner?.org_uuid,
    });

    // Clear browser cache before opening AdminPortal
    if ('caches' in window) {
      await caches
        .keys()
        .then((cacheNames) => {
          return Promise.all(
            cacheNames.map((cacheName) => {
              return caches.delete(cacheName);
            })
          );
        })
        .catch((error) => {
          logger.error('Error clearing cache:', {}, error);
        });
    }

    AdminPortal.show();
    onClose();
  };

  const handleManageBilling = () => {
    onClose();
  };

  const handleSignOut = () => {
    onClose();
    const baseUrl = ContextHolder.getContext().baseUrl;
    // eslint-disable-next-line no-undef
    analytics.track('Signed Out', {
      email: user?.email,
      orgId: currentPractitioner?.org_uuid,
    });
    RUM.clearUser();
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_id');
    localStorage.removeItem('show_current_practitioner');
    localStorage.clear();
    window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${
      isOP ? `${baseUrl}/oauth/account/login?organization=op` : window.location
    }`;
  };

  const userName = currentPractitioner?.practitioner_name || '';
  const userEmail = user?.email || '';

  const divider = <Divider className="my-2 -mx-2 w-[calc(100%+16px)]" />;

  return (
    <div>
      <div className="p-2">
        <div className="flex items-center gap-2">
          <WorkspaceLogo />
          <div className="flex flex-col items-start gap-1">
            <div className="font-semibold">{currentPractitioner?.org_name}</div>
            <WorkspacePlan />
          </div>
        </div>
      </div>
      {tenants.length > 1 && (
        <>
          {divider}
          <div className="p-2">
            <div className="font-medium flex items-center gap-2 mb-2">
              <Building03 /> Switch Organization
            </div>
            <Input
              placeholder="Search organization"
              prefix={<SearchOutlined />}
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              allowClear
              className="w-full"
            />
            <div className="flex flex-col items-start mt-2 w-full max-h-[200px] overflow-y-auto">
              {filteredTenants.map(
                (tenant: { tenantId: string; name: string }) => (
                  <div
                    className={classNames(
                      'flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-100 rounded-md w-full justify-between',
                      {
                        'bg-primary-50': tenant.tenantId === activeTenantId,
                      }
                    )}
                    key={tenant.tenantId}
                    onClick={() => handleSwitchOrg(tenant.tenantId)}
                  >
                    <span>{tenant.name}</span>
                    {tenant.tenantId === activeTenantId && (
                      <CheckOutlined className="text-primary" />
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        </>
      )}
      {divider}
      <div className="p-2 flex items-center gap-2">
        <PractitionerName
          practitioner={{
            first_name: currentPractitioner?.practitioner_first_name || '',
            last_name: currentPractitioner?.practitioner_last_name || '',
          }}
          showName={false}
          avatar={{ size: 40 }}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{userName}</div>
          <div className="text-sm text-gray-500 truncate">{userEmail}</div>
        </div>
      </div>

      {divider}

      <div>
        {currentPractitioner?.stripe_customer_id &&
          isPaymentsFeatureEntitled && (
            <MenuItem onClick={handleManageBilling}>
              <StripeBillingCustomerPortal
                name="Manage Billing"
                className={'!p-0'}
              />
            </MenuItem>
          )}
        <MenuItem onClick={handleOpenSettings}>
          <SettingOutlined />
          <span>Security Settings</span>
        </MenuItem>
        <MenuItem danger onClick={handleSignOut}>
          <LogOut02 />
          <span>Sign out</span>
        </MenuItem>
      </div>
    </div>
  );
};

export const UserMenu = () => {
  const { data: user } = useGetCurrentPractitioner();
  const [open, setOpen] = useState(false);

  return (
    <Popover
      placement="rightBottom"
      styles={{
        body: {
          padding: 8,
        },
      }}
      content={<UserMenuContent onClose={() => setOpen(false)} />}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
    >
      <span className="cursor-pointer rounded-full border-2 border-gray-200 hover:border-gray-300">
        <PractitionerName
          practitioner={{
            first_name: user?.practitioner_first_name || '',
            last_name: user?.practitioner_last_name || '',
          }}
          showName={false}
          avatar={{ size: 42 }}
        />
      </span>
    </Popover>
  );
};
